import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building2,
  FolderKanban,
  Lock,
  LogIn,
  User,
} from 'lucide-react';
import type { AxiosError } from 'axios';
import logoApp from '../../assets/images/logo.png';
import axios from '../../api/axios';
import type { ApiResponseEnvelope, LoginSuccessContent } from '../../types/auth';

type LoginProps = {
  onLogin: (role: string, options?: { prototype?: boolean }) => void;
};

type LoginErrorResponse = {
  message?: string;
};

const highlights = [
  {
    icon: Building2,
    title: 'Portal Proyek',
    body: 'Pasang kebutuhan pekerjaan, kelola proyek, dan pantau progres dalam satu tempat.',
  },
  {
    icon: Briefcase,
    title: 'Penawaran mitra',
    body: 'Kirim dan terima penawaran resmi — lebih jelas daripada chat perkiraan harga.',
  },
  {
    icon: FolderKanban,
    title: 'Satu akun Indoteknisi',
    body: 'Masuk dengan akun yang sama seperti website dan aplikasi Indoteknisi.',
  },
];

const Login = ({ onLogin }: LoginProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const bladeLoginUrl = import.meta.env.VITE_BLADE_LOGIN_URL;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post<ApiResponseEnvelope<LoginSuccessContent>>(
        '/v1/admin/auth/login',
        {
          email_or_phone: email,
          password,
        },
      );

      if (response.data.response_code === 'auth_login_200') {
        const { token, user_type: userType } = response.data.content;

        localStorage.setItem('token', token);
        onLogin(userType);
        navigate('/');
        return;
      }

      setError(response.data.message || 'Gagal masuk. Silakan coba lagi.');
    } catch (error) {
      const axiosError = error as AxiosError<LoginErrorResponse>;
      console.error('Login error:', axiosError);
      setError(
        axiosError.response?.data?.message ||
          'Terjadi kesalahan koneksi ke server.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-[#f8fafc] px-4 py-12 font-sans">
      <div className="pointer-events-none absolute -right-[5vw] -top-[5vw] h-[30vw] min-h-[300px] w-[30vw] min-w-[300px] rounded-full bg-[#4A6CF7]/25 opacity-40 blur-[8vw]" />
      <div className="pointer-events-none absolute -bottom-[5vw] -left-[5vw] h-[25vw] min-h-[250px] w-[25vw] min-w-[250px] rounded-full bg-[#2176CC]/20 opacity-30 blur-[7vw]" />

      <div className="relative z-10 flex w-full max-w-[1150px] flex-col-reverse items-center justify-center gap-12 lg:flex-row lg:gap-20">
        {/* Panel info — pola layout lama */}
        <div className="hidden flex-1 animate-fade-in flex-col items-center gap-8 text-center lg:flex lg:items-start lg:text-left">
          <div>
            <span className="inline-block rounded-full border border-solid border-blue-100 bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#3451d1]">
              Portal Proyek
            </span>
            <h2 className="mt-4 mb-3 text-3xl font-extrabold leading-tight tracking-tight text-[#1e293b] md:text-4xl lg:text-5xl">
              Solusi terintegrasi
              <br className="hidden lg:block" />
              <span className="bg-gradient-to-r from-[#3451d1] to-[#2176CC] bg-clip-text text-transparent">
                proyek & penawaran
              </span>
            </h2>
            <p className="max-w-[500px] text-base text-[#64748b] md:text-lg">
              Kelola proyek, penawaran mitra, dan pantau pengerjaan — terhubung
              dengan akun Indoteknisi Anda.
            </p>
          </div>

          <div className="flex w-full max-w-[520px] flex-col gap-4">
            {highlights.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="group flex items-start gap-4 rounded-2xl p-4 text-left transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-white hover:shadow-md hover:shadow-slate-100"
              >
                <div className="flex items-center justify-center rounded-xl border border-solid border-[#e2e8f0] bg-white p-3.5 text-[#3451d1] shadow-xs transition-all duration-300 group-hover:scale-105 group-hover:border-blue-200 group-hover:bg-blue-50/50">
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="mb-0.5 text-lg font-bold text-[#334155]">{title}</h3>
                  <p className="text-sm leading-relaxed text-[#64748b]">{body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex w-full items-center justify-center gap-8 border-t border-solid border-slate-200/60 pt-2 lg:justify-start">
            <div className="flex flex-col items-center gap-1 lg:items-start">
              <span className="text-2xl font-black text-[#1e293b]">Owner</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
                Pasang proyek
              </span>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex flex-col items-center gap-1 lg:items-start">
              <span className="text-2xl font-black text-[#1e293b]">Mitra</span>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8]">
                Kirim penawaran
              </span>
            </div>
          </div>
        </div>

        {/* Form login */}
        <div className="w-full max-w-[430px]">
          <Link
            to="/"
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#3451d1] transition hover:text-[#2176CC]"
          >
            <ArrowLeft size={16} />
            Kembali ke beranda
          </Link>

          <div className="rounded-3xl border border-solid border-white bg-white/80 p-8 shadow-xl shadow-slate-200/50 backdrop-blur-md transition-all duration-300 hover:shadow-2xl hover:shadow-slate-200/80 md:p-10">
            <div className="mb-8 text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl border border-solid border-[#f1f5f9] bg-slate-50">
                <img
                  src={logoApp}
                  alt="Indoteknisi"
                  className="h-14 w-auto object-contain"
                />
              </div>
              <h3 className="mb-1 text-xl font-extrabold tracking-tight text-[#1e293b]">
                Masuk Portal Proyek
              </h3>
              <p className="text-sm text-[#64748b]">
                Silakan masuk dengan akun Indoteknisi Anda
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#475569]">
                  Email
                </label>
                <div className="flex items-center gap-3 rounded-xl border border-solid border-[#e2e8f0] bg-white px-4 py-3.5 transition-all duration-200 focus-within:border-[#4A6CF7] focus-within:shadow-[0_0_0_4px_rgba(74,108,247,0.12)]">
                  <User size={18} className="text-[#94a3b8]" />
                  <input
                    type="text"
                    placeholder="Masukkan Email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="w-full border-0 bg-transparent text-sm font-medium text-[#334155] outline-none placeholder-[#94a3b8]"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#475569]">
                  Password
                </label>
                <div className="flex items-center gap-3 rounded-xl border border-solid border-[#e2e8f0] bg-white px-4 py-3.5 transition-all duration-200 focus-within:border-[#4A6CF7] focus-within:shadow-[0_0_0_4px_rgba(74,108,247,0.12)]">
                  <Lock size={18} className="text-[#94a3b8]" />
                  <input
                    type="password"
                    placeholder="Masukkan password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className="w-full border-0 bg-transparent text-sm font-medium text-[#334155] outline-none placeholder-[#94a3b8]"
                  />
                </div>
              </div>

              {error && (
                <div className="flex animate-fade-in items-center gap-2.5 rounded-xl border border-solid border-red-100 bg-[#fef2f2] p-3.5 text-sm font-semibold text-[#ef4444]">
                  <AlertCircle size={18} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-xl border-0 bg-[#3451d1] py-4 text-sm font-bold tracking-wide text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2176CC] hover:shadow-lg hover:shadow-blue-200 disabled:transform-none disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-[3px] border-solid border-white/30 border-t-white" />
                ) : (
                  <>
                    <LogIn size={18} />
                    <span>Masuk Sekarang</span>
                  </>
                )}
              </button>

              {bladeLoginUrl ? (
                <div className="pt-2 text-center">
                  <p className="text-sm text-[#64748b]">
                    Belum punya akun?{' '}
                    <a
                      href={bladeLoginUrl}
                      className="font-semibold text-[#3451d1] transition-colors hover:text-[#2176CC] hover:underline"
                    >
                      Daftar di Indoteknisi
                    </a>
                  </p>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
